var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
// Import style for web component
import styles from './web-component-style.scss?inline';
/**
 * Icon configuration
 */
const CONFIG = {
    // Start color
    startColor: '#79d469',
    // End color
    endColor: '#0790e8',
    // Duration of the animation
    durationMs: 3000,
};
/**
 * A Lit component that displays an AI icon.
 *
 * @summary Displays an AI icon.
 *
 * @since 7.0.0 Initial implementation
 *
 * @example Basic usage
 * This example shows a simple AI icon.
 * ```html
 * <uo-icon-ai></uo-icon-ai>
 * ```
 * <uo-icon-ai></uo-icon-ai>
 */
let IconAi = class IconAi extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * Whether the AI icon is in a loading state.
         * Triggers animation when set to true.
         *
         * @default false
         */
        this.loading = false;
        /**
         * Tracks whether the animation is currently in its "end" visual state.
         * This toggles every `CONFIG.durationMs` to drive transitions.
         */
        this._animationEndState = false;
    }
    /**
     * Called before each update. Starts or stops the animation loop when the `loading` property changes.
     * @param changed
     */
    willUpdate(changed) {
        if (changed.has('loading')) {
            if (this.loading) {
                this._startLoop();
            }
            else {
                this._stopLoop();
            }
        }
    }
    /**
     * Starts the animation loop by toggling `_animationEndState` every `durationMs` milliseconds.
     */
    _startLoop() {
        // Reset any existing interval
        this._stopLoop();
        // Trigger animation right away
        setTimeout(() => {
            this._animationEndState = !this._animationEndState;
        }, 1);
        // Run again after the defined duration
        this._interval = window.setInterval(() => {
            this._animationEndState = !this._animationEndState;
        }, CONFIG.durationMs);
    }
    /**
     * Stops the animation loop and clears the interval.
     */
    _stopLoop() {
        clearInterval(this._interval);
        this._interval = undefined;
    }
    /**
     * Renders the component's DOM structure.
     * @return The HTML template for the icon.
     */
    render() {
        const classes = {
            'icon-ai': true,
            'icon-ai--animation-end-state': this._animationEndState,
        };
        return html `
			<style>

			.icon-ai {
				--uo-icon-ai-duration-ms: ${CONFIG['durationMs']}ms;
				--uo-icon-ai-start-color: ${CONFIG['startColor']};
				--uo-icon-ai-end-color: ${CONFIG['endColor']};
			}

			</style>

			<div class=${classMap(classes)}>
				<svg id="sparkles" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 512 512">
					<defs>
						<style>
						#main {
							fill: url(#linear-gradient);
						}

						#bottom-right {
							fill: ${CONFIG['startColor']};
						}

						#top-right {
							fill: ${CONFIG['endColor']};
						}
						</style>
						
						<linearGradient id="linear-gradient" x1="190.55" y1="445.1" x2="190.55" y2="64" gradientUnits="userSpaceOnUse">
							<stop offset="0" stop-color="${CONFIG['startColor']}"/>
							<stop offset="1" stop-color="${CONFIG['endColor']}"/>
						</linearGradient>
					</defs>

					<path 
						id="main" 
						d="M205.1,73.3c-2.6-5.7-8.3-9.3-14.5-9.3s-11.9,3.6-14.5,9.3l-52.8,114L9.3,240c-5.7,2.6-9.3,8.3-9.3,14.6s3.6,11.9,9.3,14.5l114.1,52.7,52.6,114c2.6,5.7,8.3,9.3,14.5,9.3s11.9-3.6,14.5-9.3l52.7-114.1,114.1-52.7c5.7-2.6,9.3-8.3,9.3-14.5s-3.6-11.9-9.3-14.5l-114-52.6-52.7-114.1Z"
					/>

					<path 
						id="top-right" 
						d="M327.5,85.2c-4.5,1.7-7.5,6-7.5,10.8s3,9.1,7.5,10.8l56.5,21.2,21.2,56.5c1.7,4.5,6,7.5,10.8,7.5s9.1-3,10.8-7.5l21.2-56.5,56.5-21.2c4.5-1.7,7.5-6,7.5-10.8s-3-9.1-7.5-10.8l-56.5-21.2-21.2-56.5c-1.7-4.5-6-7.5-10.8-7.5s-9.1,3-10.8,7.5l-21.2,56.5-56.5,21.2Z"
					/>

					<path 
						id="bottom-right" 
						d="M384,384l-56.5,21.2c-4.5,1.7-7.5,6-7.5,10.8s3,9.1,7.5,10.8l56.5,21.2,21.2,56.5c1.7,4.5,6,7.5,10.8,7.5s9.1-3,10.8-7.5l21.2-56.5,56.5-21.2c4.5-1.7,7.5-6,7.5-10.8s-3-9.1-7.5-10.8l-56.5-21.2-21.2-56.5c-1.7-4.5-6-7.5-10.8-7.5s-9.1,3-10.8,7.5l-21.2,56.5Z"
					/>
				</svg>
			</div>
		`;
    }
};
/**
 * Defines the component's CSS styles.
 * Applies the imported SCSS styles using `unsafeCSS`.
 */
IconAi.styles = css `${unsafeCSS(styles)}`;
__decorate([
    property({ type: Boolean })
], IconAi.prototype, "loading", void 0);
__decorate([
    state()
], IconAi.prototype, "_animationEndState", void 0);
IconAi = __decorate([
    customElement('uo-icon-ai')
], IconAi);
export { IconAi };
